import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk, EntityId } from "@reduxjs/toolkit";
import mineCraftApi from '../../api/mineCraftApi';
import minecraftUrls from "../../configs/mine_craft/minecraftUrls";

const processStrategy = (value: any) => {
  return {
    ...value,
    url: minecraftUrls.categoryPath(value.target_id, value.id),
    image_url: minecraftUrls.remoteUrl(value.image_url)
  };
}

const subcategoriesEntity = new schema.Entity(
  'subcategories',
  {},
  {
    processStrategy
  }
);

const categoriesEntity = new schema.Entity(
  'categories',
  {
    subcategories: [subcategoriesEntity]
  },
  {
    processStrategy
  }
);

const categoryEntity = new schema.Entity(
  'category',
  {
    subcategories: [subcategoriesEntity]
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize(response.data.category, categoryEntity);
  const { category, subcategories} = normalized.entities;

  return {
    categories: { ...category, ...subcategories }
  }
}

export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (params: object = {}) => {
    const response = await mineCraftApi.get(`/onesky/categories`, { params })
    const normalized = normalize(response.data, [categoriesEntity]);
    const { categories, subcategories } = normalized.entities;

    return {
      categories: { ...categories, ...subcategories },
      ids: normalized.result
    };
  }
);

export const fetchCategory = createAsyncThunk(
  'categories/fetchCategory',
  async (id: number) => {
    const response = await mineCraftApi.get(`/onesky/categories/${id}`)

    return normalizeInstance(response);
  }
);

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/categories`, data)

      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/categories/${id}`, data)

      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyCategory = createAsyncThunk(
  'categories/destroyCategory',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/categories/${id}`)

    return response.data;
  }
);

export const reorderCategories = createAsyncThunk(
  'categories/reorderCategories',
  async ({ ids, reorderParentId }: { ids: any, reorderParentId: EntityId | null }, { rejectWithValue }) => {
    try {
      await mineCraftApi.patch('/onesky/categories/reorder', { ids })

      return { ids, reorderParentId };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
